import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

const LeadContext = createContext();

export const useLead = () => {
  const context = useContext(LeadContext);
  if (!context) {
    throw new Error('useLead must be used within a LeadProvider');
  }
  return context;
};

export const LeadProvider = ({ children }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setLeads(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const addLead = async (leadData) => {
    try {
      console.log("Adding lead with data:", leadData);
      // Ensure we have created_at and updated_at
      const dataWithTimestamps = {
        ...leadData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('leads')
        .insert([dataWithTimestamps]);
      
      if (error) {
        console.error("Supabase insert error:", error);
        throw error;
      }
      
      // Fetch the newly created lead to get all fields
      const { data: newLead, error: fetchError } = await supabase
        .from('leads')
        .select('*')
        .eq('created_at', dataWithTimestamps.created_at)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (fetchError) {
        console.error("Error fetching new lead:", fetchError);
        throw fetchError;
      }
      
      setLeads(prev => [newLead, ...prev]);
      return { success: true, data: newLead };
    } catch (err) {
      console.error('Error in addLead:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateLead = async (id, updates) => {
    try {
      console.log("Updating lead:", id, "with data:", updates);
      
      // Add updated_at timestamp
      const dataWithTimestamp = {
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('leads')
        .update(dataWithTimestamp)
        .eq('id', id);
      
      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }
      
      // Fetch the updated lead to get all fields
      const { data: updatedLead, error: fetchError } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .single();
        
      if (fetchError) {
        console.error("Error fetching updated lead:", fetchError);
        throw fetchError;
      }
      
      setLeads(prev => prev.map(lead => lead.id === id ? updatedLead : lead));
      return { success: true, data: updatedLead };
    } catch (err) {
      console.error('Error in updateLead:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteLead = async (id) => {
    try {
      console.log("Deleting lead:", id);
      
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }
      
      setLeads(prev => prev.filter(lead => lead.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error in deleteLead:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const addContact = async (leadId, contactData) => {
    try {
      console.log("Adding contact for lead:", leadId, "with data:", contactData);
      
      // Ensure we have contact_date and created_at
      const dataWithTimestamps = {
        lead_id: leadId,
        ...contactData,
        contact_date: new Date().toISOString(),
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('contacts')
        .insert([dataWithTimestamps]);
      
      if (error) {
        console.error("Supabase insert contact error:", error);
        throw error;
      }
      
      // Fetch the newly created contact to get all fields
      const { data: newContact, error: fetchError } = await supabase
        .from('contacts')
        .select('*')
        .eq('created_at', dataWithTimestamps.created_at)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (fetchError) {
        console.error("Error fetching new contact:", fetchError);
        throw fetchError;
      }
      
      return { success: true, data: newContact };
    } catch (err) {
      console.error('Error in addContact:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateContact = async (contactId, updates) => {
    try {
      console.log("Updating contact:", contactId, "with data:", updates);
      
      const { data, error } = await supabase
        .from('contacts')
        .update(updates)
        .eq('id', contactId);
      
      if (error) {
        console.error("Supabase update contact error:", error);
        throw error;
      }
      
      // Fetch the updated contact to get all fields
      const { data: updatedContact, error: fetchError } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', contactId)
        .single();
        
      if (fetchError) {
        console.error("Error fetching updated contact:", fetchError);
        throw fetchError;
      }
      
      return { success: true, data: updatedContact };
    } catch (err) {
      console.error('Error in updateContact:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteContact = async (contactId) => {
    try {
      console.log("Deleting contact:", contactId);
      
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId);
      
      if (error) {
        console.error("Supabase delete contact error:", error);
        throw error;
      }
      
      return { success: true };
    } catch (err) {
      console.error('Error in deleteContact:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const fetchContacts = async (leadId) => {
    try {
      console.log("Fetching contacts for lead:", leadId);
      
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('lead_id', leadId)
        .order('contact_date', { ascending: false });
      
      if (error) {
        console.error("Supabase fetch contacts error:", error);
        throw error;
      }
      
      return { success: true, data: data || [] };
    } catch (err) {
      console.error('Error in fetchContacts:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const value = {
    leads,
    loading,
    error,
    fetchLeads,
    addLead,
    updateLead,
    deleteLead,
    addContact,
    updateContact,
    deleteContact,
    fetchContacts
  };

  return (
    <LeadContext.Provider value={value}>
      {children}
    </LeadContext.Provider>
  );
};